import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_SCRIPT_LIBRARIES } from "constants/googleMapsScript";

type Prediction = google.maps.places.AutocompletePrediction;

export type AddressAutocompleteProps = {
  value: string;
  onChange: (v: string) => void;
  onSelect?: (payload: {
    addressLine1: string;
    city: string;
    cityCode: string;
    state: string;
    stateCode: string;
    zipcode: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    placeId: string;
  }) => void;
  countries?: string[];   // omit for global
  types?: string[];       // default ["address"]
  minLength?: number;     // default 3
  debounceMs?: number;    // default 120
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  error?: string;
  dropdownClassName?: string;
  style?: React.CSSProperties;
};

/** City from the clicked suggestion, e.g. "Kelley Highlands Drive, Talking Rock, GA, USA" -> "Talking Rock".
 * For US-style descriptions the city is third-from-last (before state and country). */
function cityFromPrediction(prediction?: Prediction | null): string {
  const terms = prediction?.terms;
  if (!terms || terms.length < 3) return "";
  return String(terms[terms.length - 3]?.value ?? "").trim();
}

function parseParts(
  details?: google.maps.places.PlaceResult | null,
  prediction?: Prediction | null
) {
  // Returns undefined when the component is missing so || fallback chains work
  const getComponent = (type: string) =>
    details?.address_components?.find((c) => c.types.includes(type));

  const pick = (type: string, from: "long_name" | "short_name" = "long_name") => {
    const c = getComponent(type);
    return c ? c[from] : "";
  };

  const streetNumber = pick("street_number");
  const route = pick("route");

  // City fallback chain: locality -> postal_town -> sublocality -> admin_level_3,
  // then the clicked suggestion text, then county as a last resort.
  const cityObj =
    getComponent("locality") ||
    getComponent("postal_town") ||
    getComponent("sublocality") ||
    getComponent("sublocality_level_1") ||
    getComponent("administrative_area_level_3");
  const predictionCity = cityFromPrediction(prediction);
  const countyObj = getComponent("administrative_area_level_2");
  const city = cityObj?.long_name || predictionCity || countyObj?.long_name || "";
  const cityCode = cityObj?.short_name || predictionCity || countyObj?.short_name || "";

  // State
  const stateObj = getComponent("administrative_area_level_1");
  const state = stateObj?.long_name || "";
  const stateCode = stateObj?.short_name || "";

  // Zipcode
  const zipcode = pick("postal_code");

  // Country
  const countryObj = getComponent("country");
  const country = countryObj?.long_name || "";
  const countryCode = countryObj?.short_name || "";

  const addressLine1 =
    [streetNumber, route].filter(Boolean).join(" ").trim() ||
    details?.formatted_address ||
    "";

  const latitude = details?.geometry?.location?.lat() || 0;
  const longitude = details?.geometry?.location?.lng() || 0;

  return { 
    addressLine1, 
    city, 
    cityCode,
    state, 
    stateCode,
    zipcode, 
    country, 
    countryCode,
    latitude, 
    longitude 
  };
}

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  countries,
  types = ["address"],
  minLength = 3,
  debounceMs = 120,
  id,
  name,
  placeholder = "Enter address",
  className = "form-control",
  disabled,
  autoFocus,
  autoComplete = "off",
  error,
  dropdownClassName,
  style,
}: AddressAutocompleteProps) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: String(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""),
    libraries: GOOGLE_MAPS_SCRIPT_LIBRARIES,
  });

  const svcRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const detailsRef = useRef<google.maps.places.PlacesService | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const [preds, setPreds] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  // Prevent immediate reopen after selection
  const keepClosedRef = useRef(false);
  // Track the latest request to ignore stale responses
  const requestIdRef = useRef(0);
  // Track if user is navigating with keyboard
  const isNavigatingRef = useRef(false);
  
  // Use refs for callbacks to prevent useCallback recreation
  const onChangeRef = useRef(onChange);
  const onSelectRef = useRef(onSelect);
  
  // Keep refs up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);
  
  // Memoize array props to prevent unnecessary effect re-runs
  const memoizedTypes = useMemo(() => types, [JSON.stringify(types)]);
  const memoizedCountries = useMemo(() => countries, [JSON.stringify(countries)]);

  useEffect(() => {
    if (!isLoaded) return;
    svcRef.current = new google.maps.places.AutocompleteService();
    detailsRef.current = new google.maps.places.PlacesService(document.createElement("div"));
  }, [isLoaded]);

  // Debounced predictions
  useEffect(() => {
    if (!isLoaded || !svcRef.current) return;
    const text = (value || "").trim();
    if (text.length < minLength) {
      setPreds([]);
      setOpen(false);
      setActiveIdx(-1);
      return;
    }
    
    // Increment request ID for this new request
    const currentRequestId = ++requestIdRef.current;
    
    const t = setTimeout(() => {
      // If user is actively typing (not just after selection), allow dropdown to open
      const req: google.maps.places.AutocompletionRequest = { input: text } as any;
      if (memoizedTypes?.length) (req as any).types = memoizedTypes;
      if (memoizedCountries?.length) (req as any).componentRestrictions = { country: memoizedCountries };
      svcRef.current!.getPlacePredictions(req, (res) => {
        // Ignore stale responses
        if (currentRequestId !== requestIdRef.current) return;
        
        const list = res || [];
        const isNavigating = isNavigatingRef.current;
        
        // Only open if not blocked and input is focused
        const shouldOpen = Boolean(list.length) && 
                          !keepClosedRef.current && 
                          document.activeElement === inputRef.current;
        
        // Batch all state updates together - no nested setState calls
        setPreds(list);
        setOpen(shouldOpen);
        
        // Only reset activeIdx when opening fresh (not navigating)
        if (shouldOpen && !isNavigating) {
          setActiveIdx(0);
        } else if (!shouldOpen) {
          setActiveIdx(-1);
        }
      });
    }, debounceMs);
    return () => clearTimeout(t);
  }, [value, isLoaded, minLength, debounceMs, memoizedTypes, memoizedCountries]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (listRef.current?.contains(t) || inputRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Keep highlighted option visible
  useEffect(() => {
    if (!listRef.current || activeIdx < 0) return;
    const el = listRef.current.querySelectorAll<HTMLElement>("[data-idx]")[activeIdx];
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const selectPrediction = useCallback((p: Prediction) => {
    // Immediately increment requestId to cancel any in-flight autocomplete requests
    requestIdRef.current++;
    
    // close now + block reopen
    keepClosedRef.current = true;
    setOpen(false);
    setPreds([]);
    setActiveIdx(-1);

    // show chosen text using ref
    onChangeRef.current(p.description);

    // IMPORTANT: blur immediately to remove focus and prevent reopening
    setTimeout(() => {
      inputRef.current?.blur();
    }, 0);

    if (!onSelectRef.current) {
      setTimeout(() => (keepClosedRef.current = false), 500);
      return;
    }

    detailsRef.current?.getDetails(
      { placeId: p.place_id, fields: ["address_components", "formatted_address", "geometry", "place_id"] },
      (details, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !details) {
          console.warn("AddressAutocomplete: getDetails failed, status:", status);
          // Still call onSelect with the description text and zero coords so the form isn't blocked
          const fallbackCity = cityFromPrediction(p);
          onSelectRef.current?.({
            addressLine1: p.description,
            city: fallbackCity, cityCode: fallbackCity, state: "", stateCode: "",
            zipcode: "", country: "", countryCode: "",
            latitude: 0, longitude: 0,
            placeId: p.place_id,
          });
          setTimeout(() => (keepClosedRef.current = false), 500);
          return;
        }
        const parts = parseParts(details, p);
        // Prefer geometry coords; fall back to parsed parts (some place types return LatLngLiteral)
        const lat = details?.geometry?.location?.lat?.() ?? details?.geometry?.location?.lat ?? parts.latitude ?? 0;
        const lng = details?.geometry?.location?.lng?.() ?? details?.geometry?.location?.lng ?? parts.longitude ?? 0;
        const selectedAddress = {
          addressLine1: parts.addressLine1 || p.description,
          city: parts.city || "",
          cityCode: parts.cityCode || "",
          state: parts.state || "",
          stateCode: parts.stateCode || "",
          zipcode: parts.zipcode || "",
          country: parts.country || "",
          countryCode: parts.countryCode || "",
          latitude: Number(lat) || 0,
          longitude: Number(lng) || 0,
          placeId: p.place_id,
        };
        // console.log("AddressAutocomplete selected address:", selectedAddress);
        onSelectRef.current?.(selectedAddress);
        // Keep dropdown closed longer after selection
        setTimeout(() => (keepClosedRef.current = false), 500);
      }
    );
  }, []); // No dependencies - all values accessed via refs

  // Global keyboard listener as backup for Enter key
  useEffect(() => {
    if (!open || preds.length === 0) return;
    
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only handle if dropdown is open
      if (!open || preds.length === 0) return;
      
      const target = e.target as HTMLElement;
      // Check if event is from our input or dropdown
      if (target !== inputRef.current && !listRef.current?.contains(target)) return;
      
      if (e.key === "Enter" && activeIdx >= 0 && activeIdx < preds.length) {
        e.preventDefault();
        e.stopPropagation();
        isNavigatingRef.current = false;
        const pred = preds[activeIdx];
        selectPrediction(pred);
      }
    };
    
    document.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
  }, [open, preds, activeIdx, selectPrediction]);

  // Keyboard navigation (stop submit + bubbling)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle Enter key FIRST before other logic
    if (e.key === "Enter") {
      if (open && preds.length > 0 && activeIdx >= 0 && activeIdx < preds.length) {
        e.preventDefault();
        e.stopPropagation();
        isNavigatingRef.current = false;
        selectPrediction(preds[activeIdx]);
        return;
      }
      // If dropdown is open but no selection, still prevent form submit
      if (open && preds.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
    
    // Stop parent form / Formik from handling these when dropdown is in use
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      isNavigatingRef.current = true;
    }

    if (e.key === "ArrowDown") {
      if (!open && preds.length) { 
        setOpen(true); 
        setActiveIdx(0); 
        return; 
      }
      if (open && preds.length > 0) {
        setActiveIdx((i) => {
          const nextIdx = Math.min(i + 1, preds.length - 1);
          return nextIdx;
        });
      }
      return;
    }
    
    if (e.key === "ArrowUp") {
      if (!open && preds.length) { 
        setOpen(true); 
        setActiveIdx(preds.length - 1); 
        return; 
      }
      if (open && preds.length > 0) {
        setActiveIdx((i) => {
          const prevIdx = Math.max(i - 1, 0);
          return prevIdx;
        });
      }
      return;
    }
    
    if (e.key === "Escape") {
      isNavigatingRef.current = false;
      setOpen(false);
      setActiveIdx(-1);
      return;
    }
  };

  if (!isLoaded) {
    return (
      <div className="position-relative" style={style}>
        <input id={id} name={name} disabled className={className} placeholder={placeholder} />
      </div>
    );
  }

  return (
    // onKeyDownCapture here is a second safety net against form submit while list is open
    <div
      className="position-relative"
      style={style}
      onKeyDownCapture={(e) => {
        // Only prevent if dropdown is open with predictions
        if (open && preds.length > 0 && e.key === "Enter") { 
          e.preventDefault(); 
          e.stopPropagation(); 
        }
      }}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        value={value}
        onChange={(e) => {
          // If user starts typing, allow dropdown again
          if (keepClosedRef.current && e.target.value !== value) {
            keepClosedRef.current = false;
          }
          // Clear navigation flag when typing
          isNavigatingRef.current = false;
          onChange(e.target.value);
        }}
        onFocus={() => {
          // Don't open dropdown on focus if we just selected something
          if (keepClosedRef.current) return;
          // Only open if there are predictions
          if (preds.length > 0) {
            setOpen(true);
          }
        }}
        onClick={() => {
          // Don't open dropdown on click if we just selected something
          if (keepClosedRef.current) return;
          // Only open if there are predictions
          if (preds.length > 0) {
            setOpen(true);
          }
        }}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          // Don't close if user is navigating with keyboard
          if (isNavigatingRef.current) {
            // Keep input focused for keyboard navigation
            setTimeout(() => {
              if (isNavigatingRef.current && inputRef.current) {
                inputRef.current.focus();
              }
            }, 0);
            return;
          }
          // Delay closing to allow click/touch events to fire
          setTimeout(() => {
            setOpen(false);
            setActiveIdx(-1);
          }, 150);
        }}
        placeholder={placeholder}
        className={className}
        autoComplete={autoComplete}
        autoCorrect="off"
        spellCheck={false}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? `${id || name}-addr-list` : undefined}
        aria-activedescendant={
          open && activeIdx >= 0 ? `${id || name}-addr-opt-${activeIdx}` : undefined
        }
        role="combobox"
      />

      {open && preds.length > 0 && (
        <div
          ref={listRef}
          id={`${id || name}-addr-list`}
          role="listbox"
          className={dropdownClassName}
          style={{
            position: "absolute",
            left: 0, right: 0, top: "100%",
            zIndex: 2147483647,
            marginTop: 2,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.08)",
            maxHeight: 280,
            overflowY: "auto",
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          {preds.map((p, idx) => {
            const isActive = idx === activeIdx;
            return (
              <div
                id={`${id || name}-addr-opt-${idx}`}
                key={p.place_id}
                role="option"
                aria-selected={isActive}
                data-idx={idx}
                // Use onMouseDown so it runs BEFORE input blur; preventDefault to avoid focus flicker
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); selectPrediction(p); }}
                onTouchStart={() => selectPrediction(p)}
                onMouseEnter={() => setActiveIdx(idx)}
                className="px-3 py-2"
                style={{ 
                  cursor: "pointer",
                  backgroundColor: isActive ? "#e3f2fd" : "transparent",
                  borderLeft: isActive ? "3px solid #2196f3" : "3px solid transparent",
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{ 
                  fontSize: 14, 
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "#1976d2" : "#212529"
                }}>
                  {p.structured_formatting?.main_text || p.description}
                </div>
                {p.structured_formatting?.secondary_text && (
                  <div style={{ 
                    fontSize: 12, 
                    color: isActive ? "#1976d2" : "#6b7280"
                  }}>
                    {p.structured_formatting.secondary_text}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && <div className="text-danger mt-1" style={{ fontSize: 12 }}>{error}</div>}
    </div>
  );
}